import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { Suspense, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'

import ErrorPage from '@/components/organisms/ErrorPage'
import LoadingPage from '@/components/organisms/LoadingPage'
import PageTemplate from '@/components/templates/PageTemplate'
import Providers from '@/components/templates/Providers'
import useHtmlObserver from '@/hooks/useHtmlObserver'
import { checkPromotion } from '@/services/promotion'
import type { LangCode } from '@/services/schemas'
import '@/stores/init' // Initialize store listeners for states & dealers
import { $language } from '@/stores/Language'

function App({ siteLang }: { siteLang: LangCode }) {
  const { i18n } = useTranslation()
  useHtmlObserver()

  useEffect(() => {
    $language.set(siteLang)
    // Set the language based on the siteLang prop
    if (siteLang && siteLang !== i18n.language) {
      i18n.changeLanguage(siteLang)
    }
  }, [siteLang, i18n])

  // Check for active promotions on app start
  useEffect(() => {
    // Default to 'DE' for country code - can be updated when country is selected
    checkPromotion('DE', siteLang)
  }, [siteLang])

  return (
    <Providers>
      <main id="mycontroller-main" className="min-h-screen bg-rsf-white-background">
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary
              onReset={reset}
              fallbackRender={({ resetErrorBoundary }) => (
                <ErrorPage reset={resetErrorBoundary} />
              )}
            >
              <Suspense fallback={<LoadingPage />}>
                <PageTemplate />
              </Suspense>
            </ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </main>
    </Providers>
  )
}

export default App
